// Meldingen — in-app notification center (design-vs-build Theme 4). The notification list/mark-read
// endpoints + api-client methods existed but no screen surfaced them. Read-only feed + mark-as-read.
// Backend: GET /notifications + PATCH /notifications/:id {read:true}. RLS-scoped; gated notification.read.
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Can } from "../lib/rbac";
import { useI18n } from "../lib/i18n";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface Notif { id: string; kind: string | null; title: string; body: string | null; link: string | null; readAt: string | null; createdAt: string | null }

export function NotificationsRoute() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ["notifications"], queryFn: () => api.listNotifications() as Promise<Notif[]> });
  const rows = data ?? [];
  const markRead = useMutation({
    mutationFn: (id: string) => api.markNotificationRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  return (
    <Can perm="notification.read" fallback={<PageHeader title={t("nav.notifications")} description={t("crud.readonly")} />}>
      <section>
        <PageHeader title={t("nav.notifications")} description={t("notif.sub")} />
        {isLoading && <p className="text-sm text-muted-foreground">{t("common.loading")}</p>}
        {error && <p className="text-sm text-destructive">{t("crud.failed")} {(error as Error).message}</p>}
        {!isLoading && !error && rows.length === 0 && <p className="text-sm text-muted-foreground">{t("notif.empty")}</p>}
        <div className="flex flex-col gap-2">
          {rows.map((n) => (
            <Card key={n.id} className={n.readAt ? "opacity-70" : ""}>
              <CardContent className="flex items-start justify-between gap-3 py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {!n.readAt && <Badge variant="default">{t("notif.unread")}</Badge>}
                    {n.kind && <Badge variant="secondary">{n.kind}</Badge>}
                    <span className="font-medium">{n.title}</span>
                  </div>
                  {n.body && <p className="mt-1 text-sm text-muted-foreground">{n.body}</p>}
                  <p className="mt-1 text-xs text-muted-foreground">
                    {n.createdAt ? new Date(n.createdAt).toLocaleString() : ""}
                    {n.link && <> · <a href={n.link} className="text-primary hover:underline">{t("notif.open")}</a></>}
                  </p>
                </div>
                {!n.readAt && (
                  <Button size="xs" variant="outline" disabled={markRead.isPending} onClick={() => markRead.mutate(n.id)}>
                    {t("notif.markRead")}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </Can>
  );
}
